import React, { useState, useRef, useMemo, memo, useCallback, useEffect, KeyboardEvent } from 'react';
import { PromoBanner } from '@box-types';
import { getCollectionCarouselImpressionGAConfig, HomeSection } from '@box/utils';
import { useInjector } from '@box-react/hooks/use-injector';
import { AnalyticsService, ConfigurationService, PromoBannersService } from '@box-core/services';
import { LocationsService } from '@box-delivery/services';
import { Autoplay, Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import isEqual from 'lodash-es/isEqual';
import { Swiper as SwiperType } from 'swiper/types';
import { PromoBanner as PromoBannerComponent } from '@box/component-library';
import { useVisibilityObserver } from '@box-react/hooks/use-visibility-observer';

type PromoBannersProps = {
  homeSection: HomeSection;
  isSeoLocationPage: boolean;
};

function PromoBannersComponent({ homeSection, isSeoLocationPage }: PromoBannersProps) {
  const analyticsService = useInjector().get(AnalyticsService);
  const locationsService = useInjector().get(LocationsService);
  const promoBannersService = useInjector().get(PromoBannersService);
  const configurationService = useInjector().get(ConfigurationService);

  const prevEl = useRef<HTMLButtonElement>(null);
  const nextEl = useRef<HTMLButtonElement>(null);

  const promoBanners = useMemo(() => homeSection.banners ?? [], [homeSection.banners]);
  const autoScrollInterval = configurationService.getConfiguration()?.bannersAutoScrollIntervalInMillis ?? 5000;

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const onPromoBannerEnteredViewport = (promoBanner: PromoBanner) => {
    triggerAnalyticsEvent('view_promotion', promoBanner);
  };

  const onPromoBannerClick = (promoBanner: PromoBanner) => {
    promoBannersService.openPromoBannerDetailsDialog(promoBanner, promoBanners, isSeoLocationPage);
    triggerAnalyticsEvent('select_promotion', promoBanner);
  };

  const getVisibilityObserver = useCallback(
    (promoBanner: PromoBanner) =>
      useVisibilityObserver({
        triggerOnce: true,
        threshold: 0.8,
        onEnteredViewport: () => {
          onPromoBannerEnteredViewport(promoBanner);
        }
      }),
    [onPromoBannerEnteredViewport]
  );

  const maxVisibleBullets = 6;
  const updatePaginationBullets = useCallback((activeIndex: number) => {
    const bullets = document.querySelectorAll<HTMLSpanElement>('.swiper-pagination .swiper-pagination-bullet');
    if (!bullets.length) return;

    const total = bullets.length;
    let start = Math.max(0, activeIndex - Math.floor(maxVisibleBullets / 2));
    let end = start + maxVisibleBullets;

    if (end > total) {
      end = total;
      start = Math.max(0, end - maxVisibleBullets);
    }

    bullets.forEach((bullet, i) => {
      bullet.style.display = i >= start && i < end ? 'inline-block' : 'none';
    });
  }, []);

  useEffect(() => {
    if (swiperInstance?.navigation && prevEl?.current && nextEl?.current) {
      swiperInstance.navigation.init();
      swiperInstance.navigation.update();
    }
  }, [swiperInstance?.navigation, prevEl?.current, nextEl?.current]);

  useEffect(() => {
    if (!swiperInstance) return;

    const handleChange = () => {
      updatePaginationBullets(swiperInstance.activeIndex);
    };

    swiperInstance.on('slideChange', handleChange);
    updatePaginationBullets(swiperInstance.activeIndex);

    return () => {
      swiperInstance.off('slideChange', handleChange);
    };
  }, [swiperInstance, updatePaginationBullets]);

  const setNavButtonContainerVisibility = useCallback(() => {
    if (!prevEl.current || !nextEl.current) return;

    const bothDisabled =
      nextEl.current.classList.contains('swiper-button-disabled') &&
      prevEl.current.classList.contains('swiper-button-disabled');

    if (bothDisabled) {
      nextEl.current.setAttribute('hidden', 'true');
      prevEl.current.setAttribute('hidden', 'true');
    }
  }, [prevEl, nextEl]);

  const triggerAnalyticsEvent = (eventName: string, promoBanner: PromoBanner) => {
    const isLandingPage = locationsService.isSeoLocationPage();
    const creativeSlot = isLandingPage ? 'discover_landing' : 'discover_home';
    const gaConfig = getCollectionCarouselImpressionGAConfig(creativeSlot, 'carousel', promoBanners, promoBanner);
    analyticsService.addGAEcommerceEvent(eventName, gaConfig);
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, promoBanner: PromoBanner) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onPromoBannerClick(promoBanner);
      }
    },
    [onPromoBannerClick]
  );

  return (
    <section className="swiper-section box-swiper promo-banners-component" aria-label={`Promotional Banners`}>
      <button
        ref={prevEl}
        className="box-swiper-button-inline-prev promo-banners-btn-inline-prev mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
        aria-label="Previous promotional banner"
      >
        <span className="material-icons-round material-icons">chevron_left</span>
      </button>

      {promoBanners.length > 0 && prevEl && nextEl && (
        <>
          <Swiper
            onSwiper={setSwiperInstance}
            onUpdate={setNavButtonContainerVisibility}
            modules={[Navigation, Mousewheel, Pagination, Autoplay, Keyboard]}
            navigation={{
              prevEl: prevEl?.current,
              nextEl: nextEl?.current
            }}
            mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
            pagination={{
              el: '.swiper-pagination',
              clickable: true,
              dynamicBullets: promoBanners.length > 6 ? true : false,
              renderBullet: (index: number, className: string): string => {
                const duration = `${autoScrollInterval}ms`;
                return `<span class="${className} ${
                  promoBanners.length < 7 ? ' non-dynamic-bullet' : ''
                }"><span class="bullet-fill" style="--duration: ${duration};"></span></span>`;
              }
            }}
            slidesPerView={'auto'}
            grabCursor={true}
            spaceBetween={16}
            autoplay={{
              delay: autoScrollInterval,
              disableOnInteraction: false
            }}
            keyboard={{
              enabled: true
            }}
          >
            <div className="swiper-wrapper" role="navigation" aria-label={`Promotional banners`}>
              {promoBanners.map((promoBanner: PromoBanner) => {
                const ref = getVisibilityObserver(promoBanner);
                return (
                  <SwiperSlide
                    key={promoBanner.slug}
                    className="swiper-slide"
                    onClick={() => onPromoBannerClick(promoBanner)}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleKeyDown(e, promoBanner)}
                    role="link"
                    tabIndex={0}
                  >
                    <PromoBannerComponent ref={ref} promoBanner={promoBanner}></PromoBannerComponent>
                  </SwiperSlide>
                );
              })}
            </div>
          </Swiper>
          <div className="promo-banners-swiper-pagination-container">
            <div className="swiper-pagination" />
          </div>
        </>
      )}

      <button
        ref={nextEl}
        className="box-swiper-button-inline-next mdc-icon-button promo-banners-btn-inline-next mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
        aria-label="Next promotional banner"
      >
        <span className="material-icons-round material-icons">chevron_right</span>
      </button>
    </section>
  );
}

// secures from unecessary rerenders due to bad state management from the parent
const areEqual = (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
};
export default memo(PromoBannersComponent, areEqual);
